import React from 'react';
import styled from 'styled-components';
import accounting from 'accounting';
import MoneyInput from 'components/shared/MoneyInputComponent';
import { PrimaryButton } from 'styles/Buttons';
import { FlexRow, FlexColumn } from 'styles/Layout';
import { get } from 'lodash';

const HOMIE_IVA = 0.16;

const Wrapper = styled.div`
  background-color: #ffffff;
`;

const WrapperComponent = styled(FlexColumn)`
  width: 300px;
  margin: 0 auto;
`;

const Row = styled(FlexRow)`
  background-color: ${({ bgColor = '#F9F9F9' }) => bgColor};
  min-height: 30px;
  border-radius: 2px;
  padding: 0 15px;
`;

const Text = styled.p`
  font-size: ${({ size = '14px' }) => size};
  font-weight: ${({ weight = 'normal' }) => weight};
  margin: ${({ margin = '0px' }) => margin};
  color: ${({ color = '#4a4a4a' }) => color};
  text-align: ${({ align = 'left' }) => align};
`;

const Button = styled(PrimaryButton)`
  text-align: center;
  width: 200px;

  :disabled {
    background-color: #e9e9e9;
  }
`;

type PropsTypes = {
  homieFirstFee: number,
  homieMonthlyFee: number,
  price: number,
  extra_services_amount: number,
  minimal_rent_months: number,
  deposit_months_warranty: number,
  recommendedPrice: number,
  handleCalculate: () => void,
  disabled: boolean
};

type StateTypes = {
  price: number,
  extra_services_amount: number,
  minimal_rent_months: number,
  deposit_months_warranty: number,
  recommended_price: number,
  profitCalculated: boolean,
  originalValue: number,
  originalValueRestMonth: number,
  profitFirstMonth: number,
  profitMonthOfMonth: number,
  totalProfitYear: number
};

export default class CalculatorEarnings extends React.Component<
  PropsTypes,
  StateTypes
> {
  state = {
    price: this.props.price || '',
    extra_services_amount: this.props.extra_services_amount || '',
    minimal_rent_months: this.props.minimal_rent_months || 6,
    deposit_months_warranty: this.props.deposit_months_warranty || 1,
    recommended_price: this.props.recommendedPrice || 0,

    profitCalculated: false,
    originalValue: 0,
    originalValueRestMonth: 0,
    profitFirstMonth: 0,
    profitMonthOfMonth: 0,
    totalProfitYear: 0
  };

  static defaultProps = {
    homieFirstFee: 0,
    homieMonthlyFee: 0.09,
    price: 0,
    extra_services_amount: 0,
    minimal_rent_months: 0,
    deposit_months_warranty: 0,
    recommended_price: 0
  };

  numberFormat = value => {
    return accounting.formatNumber(value, 2, ',', '.');
  };

  getIva = amount => {
    return amount * HOMIE_IVA;
  };

  onValidChange = (ev: string, v: string) => {
    this.setState(
      prevState => {
        prevState[v] = ev;
        return prevState;
      },
      () => {
        this.handleCalculate();
      }
    );

    if (v === 'extra_services_amount' || v === 'price') {
      this.setProfitCalculated(false);
    }
  };

  canEnableButton = () => {
    let { price, profitCalculated } = this.state;
    return !(price > 0 && profitCalculated === false);
  };

  calculateProfit = () => {
    let homieFirstFee = get(this.props, 'homieFirstFee', 0);
    let homieMonthlyFee = get(this.props, 'homieMonthlyFee', 0);

    let price = get(this.state, 'price', 0); // Precio de la renta mensual
    let extra_services_amount = get(this.state, 'extra_services_amount', 0); // Cuota de mantenimiento
    let minimal_rent_months = get(this.state, 'minimal_rent_months', 0); // Periodo minimo de renta

    let monthly_rent = Number.isInteger(parseInt(price)) ? parseInt(price) : 0;
    let maintenance = Number.isInteger(parseInt(extra_services_amount))
      ? parseInt(extra_services_amount)
      : 0;

    if (price <= 0) {
      return null;
    }

    const monthly_payment = monthly_rent + maintenance;
    let first_month_commission = monthly_payment * homieFirstFee;
    let first_month =
      monthly_payment -
      (first_month_commission + this.getIva(first_month_commission));
    let rest_months_commission = monthly_payment * homieMonthlyFee;
    let rest_of_months =
      monthly_payment -
      (rest_months_commission + this.getIva(rest_months_commission));
    let total = rest_of_months * (minimal_rent_months - 1) + first_month;

    this.setState(
      {
        originalValue: monthly_payment,
        profitFirstMonth: first_month,
        profitMonthOfMonth: rest_of_months,
        originalValueRestMonth: monthly_payment,
        totalProfitYear: total
      },
      () => {
        this.handleCalculate();
      }
    );
  };

  handleCalculate = () => {
    let {
      price,
      extra_services_amount,
      totalProfitYear,
      profitCalculated
    } = this.state;
    this.props.handleCalculate(
      totalProfitYear > 0 && price > 0 && profitCalculated === true,
      price,
      extra_services_amount
    );
  };

  renderResultCalculationProfit = () => {
    if (!this.state.profitCalculated) {
      return null;
    }

    let {
      originalValue,
      profitFirstMonth,
      profitMonthOfMonth,
      originalValueRestMonth,
      totalProfitYear
    } = this.state;

    let { homieFirstFee, homieMonthlyFee } = this.props;

    originalValue = this.numberFormat(originalValue);
    profitFirstMonth = this.numberFormat(profitFirstMonth);
    profitMonthOfMonth = this.numberFormat(profitMonthOfMonth);
    originalValueRestMonth = this.numberFormat(originalValueRestMonth);
    totalProfitYear = this.numberFormat(totalProfitYear);

    return (
      <div>
        <Text weight="bold" margin="30px 0 0" size="18px" align="center">
          Ganancia total en un año
        </Text>
        <Text weight="bold" margin="0 0 10px" size="18px" align="center">
          ${totalProfitYear} MXN
        </Text>
        <Row alignItems="center" justifyContent="space-between">
          <Text>Primer mes</Text>
          <Text>${profitFirstMonth} MXN</Text>
        </Row>
        <Text align="right" margin="10px 0" color="#7C7C7C">
          {originalValue} - {homieFirstFee * 100}% de (Comisión Homie)
        </Text>
        <Row alignItems="center" justifyContent="space-between">
          <Text>Mes a mes</Text>
          <Text>${profitMonthOfMonth} MXN</Text>
        </Row>
        <Text align="right" margin="10px 0" color="#7C7C7C">
          ${originalValueRestMonth} - {homieMonthlyFee * 100}% + IVA (Comisión
          Homie)
        </Text>
      </div>
    );
  };

  setProfitCalculated = (status: boolean) => {
    this.setState({
      profitCalculated: status
    });
  };

  onClickButtonCalculateEarnings = e => {
    this.setProfitCalculated(true);
    this.calculateProfit();
  };

  render() {
    let recommended_price = Number.isInteger(
      parseInt(this.props.recommendedPrice)
    )
      ? parseInt(this.props.recommendedPrice)
      : 0;
    recommended_price = this.numberFormat(recommended_price);
    return (
      <Wrapper>
        <WrapperComponent>
          <Text size="18px" weight="bold" margin="0 0 10px">
            Corrobora el precio del inmueble
          </Text>
          <Text size="18px" color="#278C7E" weight="bold">
            ${recommended_price} MXN + servicio
          </Text>
          <Text color="#7C7C7B" align="left" margin="0 0 10px">
            Este es el precio que nos proporcionó el inquilino
          </Text>
          <Text size="18px" margin="0 0 10px">
            Precio de la renta mensual
          </Text>
          <MoneyInput
            textAlign="left"
            name={'price'}
            value={this.state.price}
            onValueChange={this.onValidChange}
            currency={'MXN'}
            currencySign={'$'}
            disabled={this.props.disabled}
          />
          <Text size="18px" margin="0 0 10px">
            Cuota de mantenimiento
          </Text>
          <MoneyInput
            textAlign="left"
            name={'extra_services_amount'}
            value={this.state.extra_services_amount}
            onValueChange={this.onValidChange}
            currency={'MXN'}
            currencySign={'$'}
            disabled={this.props.disabled}
          />
          <Row bgColor="#ffffff" justifyContent="center">
            <Button
              onClick={this.onClickButtonCalculateEarnings}
              disabled={this.canEnableButton()}
            >
              Calcular Ganancias
            </Button>
          </Row>
          {this.renderResultCalculationProfit()}
        </WrapperComponent>
      </Wrapper>
    );
  }
}
